// ---------------------------------------------------------------------------------------------
// EPOD - the AL team's approval actions for ONE grid row, in one place.
//
// The grid's single "AL Action" column renders whatever this returns, so every approval the AL
// team can owe on an LR appears together, each button labelled by what it decides, with a hover
// tooltip saying what it does. A later approval step is added by pushing one more entry here - no
// new column, no new component.
//
// Nothing in here decides anything: every button calls a handler on epodScreen.js
// (onClickEpodGateAction / onClickEpodGateReject / onClickAlReview / onClickOcrReview), and the
// server re-checks every decision. An LR can legitimately owe two
// approvals at once (e.g. a held POD re-upload AND a dealer's Shortage claim), which is why this
// returns a list.
// ---------------------------------------------------------------------------------------------
import { getOcrReviewState } from "./workflowStatus";

export function getAlActions(row, parent) {
    var out = [];
    if (!row || !parent) return out;
    var state = parent.state || {};

    // 1. A held e-POD upload (late upload, or an OCR re-upload that needs approval) - Phase 2.
    //    Shown only to a caller the backend says may decide it (epodGateCanReview), and only for the
    //    actions epod_late_upload.approval_actions enables.
    if (row.epod_gate_status === 'late_pending_approval' && state.epodGateEnabled && state.epodGateCanReview) {
        var enabled = state.epodGateActions || {};
        var gate = row.epod_gate || {};
        var held = gate.approval_reason ? ' Held because: ' + gate.approval_reason : '';
        var isRetry = !!(gate.ocr_retry && (gate.ocr_retry.outcome === 'limit_reached' || gate.ocr_retry.outcome === 'window_exceeded'));
        var busy = state.epodGateActionBusyRowId === row._id;
        if (enabled.approve === true) {
            out.push({
                key: 'pod_approve',
                label: busy ? 'Submitting...' : 'Approve POD',
                variant: 'success',
                disabled: busy,
                title: (isRetry
                    ? 'Approve POD - allow this re-upload to go through OCR verification again.'
                    : 'Approve POD - accept this late e-POD upload and let it continue to OCR verification.') + held,
                onClick: function () { parent.onClickEpodGateAction('approve', row); },
            });
        }
        if (enabled.reject === true) {
            out.push({
                key: 'pod_reject',
                label: 'Reject POD',
                variant: 'danger',
                disabled: false,
                title: 'Reject POD - the LSP must upload a new e-POD. Remarks are required.' + held,
                onClick: function () { parent.onClickEpodGateReject(row); },
            });
        }
    }

    // 1b. An e-POD that failed OCR on its last allowed upload - Phase 2. Same who-may-decide rule as
    //     1 (epodGateCanReview); both buttons open the remarks modal (parent.onClickOcrReview).
    if (state.epodGateEnabled && state.epodGateCanReview && parent.ocrReviewOpts) {
        var reviewOpts = parent.ocrReviewOpts();
        var ocrReview = getOcrReviewState(row, reviewOpts);
        if (ocrReview && ocrReview.pending) {
            var ocrInfo = (row.epod_ocr || {}).failure_reason;
            var why = ocrReview.reason === 'ocr_could_not_read'
                ? ' OCR could not read the document.'
                : ' OCR failed on upload ' + ocrReview.upload_no + ', the last one allowed without AL approval.';
            var detail = why + (ocrInfo ? ' OCR result: ' + ocrInfo : '');
            var reviewActions = reviewOpts.actions || {};
            if (reviewActions.approve === true) {
                out.push({
                    key: 'ocr_approve',
                    label: 'Approve POD (OCR)',
                    variant: 'success',
                    disabled: false,
                    title: 'Approve POD (OCR) - accept this e-POD as verified without OCR. Remarks are required.' + detail,
                    onClick: function () { parent.onClickOcrReview('approve', row); },
                });
            }
            if (reviewActions.reject === true) {
                out.push({
                    key: 'ocr_reject',
                    label: 'Reject POD (OCR)',
                    variant: 'danger',
                    disabled: false,
                    title: 'Reject POD (OCR) - the LSP must upload a new e-POD. Remarks are required.' + detail,
                    onClick: function () { parent.onClickOcrReview('reject', row); },
                });
            }
        }
    }

    // 2. A dealer's Shortage / Damage claim waiting for AL review - Phase 4. The queue map is only
    //    ever loaded for a login allowed to see it (isAlReviewVisible), and the Approve / Reject
    //    decision itself is made inside the existing review window, where the claim's details,
    //    remarks and the terminal-rejection confirmation live.
    var qrow = (state.alReviewQueueMap || {})[String(row._id)];
    if (qrow && qrow.dealer_confirmation_status === 'pending_al_review' && parent.isAlReviewVisible && parent.isAlReviewVisible()) {
        var condition = qrow.dealer_confirmation_condition ? qrow.dealer_confirmation_condition : 'dealer';
        out.push({
            key: 'ndv_review',
            label: 'Review NDV',
            variant: 'primary',
            disabled: false,
            title: 'Review NDV - open the dealer\'s ' + condition + ' claim to approve or reject it.',
            onClick: function () { parent.onClickAlReview(qrow); },
        });
    }

    return out;
}
